/**
 * Performance monitoring and optimization utilities for session management
 */

import { logPerformance } from './session-logger';

// Performance metrics collection
const performanceMetrics = {
  cookieOperations: [],
  navigationTimes: [],
  sessionRestoreTimes: [],
  persistenceOperations: []
};

// Performance thresholds (in milliseconds)
const PERFORMANCE_THRESHOLDS = {
  cookieOperation: 10,
  navigation: 100,
  sessionRestore: 50,
  persistence: 20
};

/**
 * Measure and log cookie operation performance
 * @param {string} operation - Operation name
 * @param {Function} fn - Function to measure
 * @returns {*} - Function result
 */
export const measureCookieOperation = async (operation, fn) => {
  const startTime = performance.now();
  
  try {
    const result = await fn();
    const duration = performance.now() - startTime;
    
    performanceMetrics.cookieOperations.push({
      operation,
      duration,
      timestamp: Date.now(),
      success: true
    });
    
    if (duration > PERFORMANCE_THRESHOLDS.cookieOperation) {
      logPerformance.cookieOperation?.(operation, duration);
    }
    
    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    
    performanceMetrics.cookieOperations.push({
      operation,
      duration,
      timestamp: Date.now(),
      success: false,
      error: error.message
    });
    
    throw error;
  }
};

/**
 * Measure navigation performance
 * @param {string} url - Target URL
 * @param {Function} navigationFn - Navigation function
 * @returns {*} - Navigation result
 */
export const measureNavigation = async (url, navigationFn) => {
  const startTime = performance.now();
  
  try {
    const result = await navigationFn();
    const duration = performance.now() - startTime;
    
    performanceMetrics.navigationTimes.push({
      url,
      duration,
      timestamp: Date.now(),
      success: true
    });
    
    logPerformance.navigationTime(url, duration);
    
    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    
    performanceMetrics.navigationTimes.push({
      url,
      duration,
      timestamp: Date.now(),
      success: false,
      error: error.message
    });
    
    throw error;
  }
};

/**
 * Measure session restoration performance
 * @param {Function} restoreFn - Restoration function
 * @returns {*} - Restoration result
 */
export const measureSessionRestore = async (restoreFn) => {
  const startTime = performance.now();
  
  try {
    const result = await restoreFn();
    const duration = performance.now() - startTime;
    
    performanceMetrics.sessionRestoreTimes.push({
      duration,
      timestamp: Date.now(),
      success: true,
      restored: result
    });
    
    logPerformance.sessionRestoreTime(duration);
    
    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    
    performanceMetrics.sessionRestoreTimes.push({
      duration,
      timestamp: Date.now(),
      success: false,
      error: error.message
    });
    
    throw error;
  }
};

/**
 * Measure persistence operation performance
 * @param {string} operation - Operation name (save/load/cleanup)
 * @param {Function} persistenceFn - Persistence function
 * @returns {*} - Operation result
 */
export const measurePersistence = async (operation, persistenceFn) => {
  const startTime = performance.now();
  
  try {
    const result = await persistenceFn();
    const duration = performance.now() - startTime;
    
    performanceMetrics.persistenceOperations.push({
      operation,
      duration,
      timestamp: Date.now(),
      success: true
    });
    
    if (duration > PERFORMANCE_THRESHOLDS.persistence) {
      console.debug(`💾 Persistence: ${operation} took ${duration}ms`);
    }
    
    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    
    performanceMetrics.persistenceOperations.push({
      operation,
      duration,
      timestamp: Date.now(),
      success: false,
      error: error.message
    });
    
    throw error;
  }
};

/**
 * Get performance summary
 * @returns {Object} - Performance metrics summary
 */
export const getPerformanceSummary = () => {
  const now = Date.now();
  const oneHourAgo = now - (60 * 60 * 1000);
  
  // Filter recent metrics (last hour)
  const recentCookieOps = performanceMetrics.cookieOperations.filter(m => m.timestamp > oneHourAgo);
  const recentNavigations = performanceMetrics.navigationTimes.filter(m => m.timestamp > oneHourAgo);
  const recentRestores = performanceMetrics.sessionRestoreTimes.filter(m => m.timestamp > oneHourAgo);
  const recentPersistence = performanceMetrics.persistenceOperations.filter(m => m.timestamp > oneHourAgo);
  
  const calculateStats = (metrics) => {
    if (metrics.length === 0) return { count: 0, avg: 0, min: 0, max: 0, successRate: 0 };
    
    const durations = metrics.map(m => m.duration);
    const successCount = metrics.filter(m => m.success).length;
    
    return {
      count: metrics.length,
      avg: durations.reduce((a, b) => a + b, 0) / durations.length,
      min: Math.min(...durations),
      max: Math.max(...durations),
      successRate: (successCount / metrics.length) * 100
    };
  };
  
  return {
    timestamp: new Date().toISOString(),
    timeRange: 'Last 1 hour',
    cookieOperations: calculateStats(recentCookieOps),
    navigation: calculateStats(recentNavigations),
    sessionRestore: calculateStats(recentRestores),
    persistence: calculateStats(recentPersistence),
    thresholds: PERFORMANCE_THRESHOLDS,
    recommendations: generatePerformanceRecommendations({
      cookieOperations: recentCookieOps,
      navigation: recentNavigations,
      sessionRestore: recentRestores,
      persistence: recentPersistence
    })
  };
};

/**
 * Generate performance recommendations
 * @param {Object} metrics - Recent metrics
 * @returns {Array} - Array of recommendations
 */
const generatePerformanceRecommendations = (metrics) => {
  const recommendations = [];
  
  // Check cookie operation performance
  const slowCookieOps = metrics.cookieOperations.filter(m => m.duration > PERFORMANCE_THRESHOLDS.cookieOperation);
  if (slowCookieOps.length > 0) {
    recommendations.push({
      category: 'cookies',
      severity: 'warning',
      message: `${slowCookieOps.length} slow cookie operations detected`,
      suggestion: 'Consider using batch cookie operations or checking for browser issues'
    });
  }
  
  // Check navigation performance
  const slowNavigations = metrics.navigation.filter(m => m.duration > PERFORMANCE_THRESHOLDS.navigation);
  if (slowNavigations.length > 0) {
    recommendations.push({
      category: 'navigation',
      severity: 'warning',
      message: `${slowNavigations.length} slow navigations detected`,
      suggestion: 'Verify network connectivity and consider preloading resources'
    });
  }
  
  // Check session restore performance
  const slowRestores = metrics.sessionRestore.filter(m => m.duration > PERFORMANCE_THRESHOLDS.sessionRestore);
  if (slowRestores.length > 0) {
    recommendations.push({
      category: 'session-restore',
      severity: 'info',
      message: `${slowRestores.length} slow session restores detected`,
      suggestion: 'Consider optimizing localStorage access or reducing session data size'
    });
  }
  
  // Check persistence performance
  const slowPersistence = metrics.persistence.filter(m => m.duration > PERFORMANCE_THRESHOLDS.persistence);
  if (slowPersistence.length > 0) {
    recommendations.push({
      category: 'persistence',
      severity: 'info',
      message: `${slowPersistence.length} slow persistence operations detected`,
      suggestion: 'Consider reducing session backup frequency or data size'
    });
  }
  
  // Check success rates
  const cookieSuccessRate = metrics.cookieOperations.length > 0 ? 
    (metrics.cookieOperations.filter(m => m.success).length / metrics.cookieOperations.length) * 100 : 100;
  
  if (cookieSuccessRate < 95) {
    recommendations.push({
      category: 'reliability',
      severity: 'error',
      message: `Cookie operation success rate is ${cookieSuccessRate.toFixed(1)}%`,
      suggestion: 'Check browser compatibility and HTTPS configuration'
    });
  }
  
  if (recommendations.length === 0) {
    recommendations.push({
      category: 'general',
      severity: 'success',
      message: 'All session operations are performing within acceptable thresholds',
      suggestion: 'Continue monitoring for any performance degradation'
    });
  }
  
  return recommendations;
};

/**
 * Clear old performance metrics to prevent memory leaks
 */
export const cleanupPerformanceMetrics = () => {
  const oneHourAgo = Date.now() - (60 * 60 * 1000);
  
  performanceMetrics.cookieOperations = performanceMetrics.cookieOperations.filter(m => m.timestamp > oneHourAgo);
  performanceMetrics.navigationTimes = performanceMetrics.navigationTimes.filter(m => m.timestamp > oneHourAgo);
  performanceMetrics.sessionRestoreTimes = performanceMetrics.sessionRestoreTimes.filter(m => m.timestamp > oneHourAgo);
  performanceMetrics.persistenceOperations = performanceMetrics.persistenceOperations.filter(m => m.timestamp > oneHourAgo);
};

/**
 * Start performance monitoring (call this once during app initialization)
 */
export const startPerformanceMonitoring = () => {
  // Clean up old metrics every 15 minutes
  setInterval(cleanupPerformanceMetrics, 15 * 60 * 1000);
  
  console.info('🚀 Session Performance: Monitoring started');
};

/**
 * Export performance utilities to window for debugging
 */
export const exposePerformanceUtilities = () => {
  if (typeof window !== 'undefined') {
    window.olivsSessionPerformance = {
      getSummary: getPerformanceSummary,
      cleanup: cleanupPerformanceMetrics,
      metrics: performanceMetrics,
      thresholds: PERFORMANCE_THRESHOLDS
    };
  }
};